import camelcaseKeys from 'camelcase-keys';
import { ofetch, type FetchResponse, type FetchError, type FetchOptions } from 'ofetch';
import '../../types/ofetch.d';
import { HTTP_DEFAULT_TIMEOUT } from '../constants';
import { ApiError } from '../models/ApiError';
import { HttpClient, type HttpRequestOptions, type HttpResponse } from './HttpClient';

/**
 * HTTP client implementation using 'ofetch' library.
 */
export class OfetchHttpClient extends HttpClient {
  private readonly client: typeof ofetch;

  constructor(baseUrl: string, defaultHeaders: Record<string, string> = {}, defaultTimeout: number = HTTP_DEFAULT_TIMEOUT) {
    super(baseUrl, defaultHeaders, defaultTimeout);
    this.client = ofetch.create({
      baseURL: this.baseUrl,
      headers: this.defaultHeaders,
      timeout: this.defaultTimeout,
      credentials: 'omit', // Omit cookies from all requests: the app uses Bearer tokens for auth
      onResponse: [
        this.convertResponseKeysToCamelCase,
      ],
    });
  }

  public getBaseUrl(): string {
    return this.baseUrl;
  }

  public getDefaultHeaders(): Record<string, string> {
    return this.defaultHeaders;
  }

  get<T>(path: string, options?: HttpRequestOptions): Promise<HttpResponse<T>> {
    return this.request<T>(path, 'GET', undefined, options);
  }

  post<T>(path: string, body?: FetchOptions['body'], options?: HttpRequestOptions): Promise<HttpResponse<T>> {
    return this.request<T>(path, 'POST', body, options);
  }

  put<T>(path: string, body?: FetchOptions['body'], options?: HttpRequestOptions): Promise<HttpResponse<T>> {
    return this.request<T>(path, 'PUT', body, options);
  }

  delete<T>(path: string, options?: HttpRequestOptions): Promise<HttpResponse<T>> {
    return this.request<T>(path, 'DELETE', undefined, options);
  }

  patch<T>(path: string, body?: FetchOptions['body'], options?: HttpRequestOptions): Promise<HttpResponse<T>> {
    return this.request<T>(path, 'PATCH', body, options);
  }

  private buildRequestOptions(
    method: string,
    body?: FetchOptions['body'],
    options?: HttpRequestOptions,
  ): FetchOptions {
    const authHeaders: Record<string, string> = this.authToken ? { 'Authorization': `Bearer ${this.authToken}` } : {};

    const requestOptions: FetchOptions = {
      method,
      body,
      camelCase: options?.camelCase,
      query: options?.query,
      headers: {
        ...authHeaders,
        ...options?.headers,
      },
      timeout: options?.timeout,
      retry: options?.retry,
      retryDelay: options?.retryDelay,
      retryStatusCodes: options?.retryStatusCodes,
    };

    this.setResponseType(requestOptions, options?.responseType);

    return requestOptions;
  }

  private setResponseType(requestOptions: FetchOptions, responseType?: HttpRequestOptions['responseType']): void {
    // Only set responseType if it's not the default 'json'
    if (responseType && responseType !== 'json') {
      requestOptions.responseType = responseType;
    }
  }

  private async request<T>(
    path: string,
    method: string,
    body?: FetchOptions['body'],
    options?: HttpRequestOptions,
  ): Promise<HttpResponse<T>> {
    const requestOptions = this.buildRequestOptions(method, body, options);

    try {
      const res = await this.client.raw<T>(path, requestOptions as FetchOptions<'json'>);

      return {
        data: res._data!, // If `undefined` is possible, then `T` should include it.
        status: res.status,
      };
    } catch (error) {
      this.handleError(error as FetchError);
    }
  }

  private handleError(error: FetchError): never {
    const DEFAULT_ERROR_STATUS = 500;
    const status = error.response?.status || DEFAULT_ERROR_STATUS;
    const data = error.data || error.message;

    throw new ApiError(error.message, status, data);
  }

  private convertResponseKeysToCamelCase({ response, options }: { response: FetchResponse<Record<string, unknown>>, options: FetchOptions }) {
    const shouldConvert = options.camelCase !== false;
    if (!shouldConvert) return;

    if (response._data != null && typeof response._data === 'object') {
      response._data = camelcaseKeys(response._data, { ...(typeof options.camelCase === 'object' ? options.camelCase : {}), deep: true });
    }
  }
}
